import React from 'react';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';
import { AppBar, Dialog, IconButton, Typography, Toolbar } from '@mui/material';

// local imports
import { ButtonStyle, Module } from 'types/AgilityTypes';
import { callApi } from 'services/apiWrapper';
import { logClientException } from 'appinsights/clientAppInsights';
import PleaseWait from 'components/common/PleaseWait';
import CommonButton from 'components/common/CommonButton';

export type RealityCoachIntegratorFields = {
  launchButtonText: string;
  launchButtonStyle: ButtonStyle;
  launchButtonStyle_ValueField: string;
  openIniFrame: string;
  iFrameTitleBarText?: string;
};

const RealityCoachIntegrator = ({
  module,
}: {
  module: Module<RealityCoachIntegratorFields>;
}) => {
  const [isLoading, setIsLoading] = React.useState(false);
  const [viewDialogOpen, setViewDialogOpen] = React.useState(false);
  const [coachUrl, setCoachUrl] = React.useState('');
  const fields: RealityCoachIntegratorFields = module?.fields || {
    launchButtonText: 'Start Reality Coach',
    launchButtonStyle: {} as ButtonStyle,
    launchButtonStyle_ValueField: '',
    openIniFrame: 'false',
    iFrameTitleBarText: 'Reality Coach',
  };
  const launchButtonStyle = fields?.launchButtonStyle || {};

  const launchRealityCoach = async () => {
    try {
      setIsLoading(true);
      const data = await callApi('/api/reality-coach/launch', 'POST');
      if (data && data.redirectUrl) {
        if (fields.openIniFrame === 'true') {
          // Open in an iframe if specified
          setCoachUrl(data.redirectUrl);
          setViewDialogOpen(true);
          return;
        } else {
          window.open(data.redirectUrl, '_blank', 'noopener,noreferrer');
          return;
        }
      }
      throw new Error('Unexpected response format from Reality Coach launch');
    } catch (error) {
      console.error('Error launching Reality Coach:', error);
      logClientException(error);
      enqueueSnackbar('Reality Coach launch failed. Please try again.', {
        variant: 'error',
        action: (key) => (
          <button onClick={() => closeSnackbar(key)}>Dismiss</button>
        ),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDialogClose = () => {
    setViewDialogOpen(false);
    setCoachUrl('');
  };

  return (
    <div className="flex justify-center items-center h-full m-4">
      <PleaseWait isLoading={isLoading} />
      <div className="flex flex-col items-center">
        <CommonButton
          text={fields?.launchButtonText}
          buttonStyle={launchButtonStyle}
          onClick={launchRealityCoach}
          dataTestId="reality-coach-launch-button"
        />

        <Dialog fullScreen open={viewDialogOpen} onClose={handleDialogClose}>
          <AppBar sx={{ position: 'relative' }}>
            <Toolbar>
              <IconButton
                edge="start"
                color="inherit"
                onClick={handleDialogClose}
                aria-label="close"
              >
                <CloseIcon />
              </IconButton>
              <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                {fields.iFrameTitleBarText}
              </Typography>
            </Toolbar>
          </AppBar>
          {coachUrl && (
            <iframe
              src={coachUrl}
              style={{ width: '100%', height: '100%', border: 'none' }}
              title="Reality Coach"
              allow="camera;microphone;"
            />
          )}
        </Dialog>
      </div>
    </div>
  );
};

export default RealityCoachIntegrator;
